<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2025 (original work) Open Assessment Technologies SA ;
    import { createEventDispatcher, tick } from 'svelte';
    import { IconBarButton } from '@oat-sa-private/ui-elements';
    import { __, focusTrap } from '@oat-sa-private/ui-core';

    const dispatch = createEventDispatcher();

    export let expanded = false; /* true for expanded drawer, false for collapsed */
    export let side =
        'start'; /* 'start' or 'end'. 'start' will be for left in LTR languages and right in RTL languages */
    export let pushMode = false; /* true for push mode, false for overlay mode */
    export let id =
        'navigation-menu'; /* Unique identifier for the navigation-container, used for accessibility and targeting */
    export let role = void 0; /* ARIA role for the navigation container */
    export let ariaLabel = void 0;
    export let ariaLabelledBy = void 0;

    // DOM Elements
    let drawerCloserElement; // Reference to the drawer closer button element
    let previouslyFocused; // Store the previously focused element to return focus when closing the drawer

    // Update focused element whenever expanded state or pushMode change
    $: {
        if (expanded && !pushMode) {
            // Store the previously focused element before focusing the drawer
            previouslyFocused = document && document.activeElement;
            // Wait for DOM updates before focusing
            tick().then(() => {
                // If the drawer is expanded, focus the closer button
                if (drawerCloserElement?.children?.[0]) {
                    drawerCloserElement.children[0].focus();
                }
            });
        } else if (!expanded && previouslyFocused) {
            // Return focus to the previously focused element when closing from overlay mode
            previouslyFocused.focus();
            // Clear the reference so it's not reused incorrectly on subsequent interactions
            previouslyFocused = void 0;
        }
    }

    /**
     * Dispatch an action taken to either expand or collapse the drawer. Note that this does not change the `expanded` state directly.
     * The parent component can listen to this event to update its expanded state accordingly.
     * This function is called when the drawer toggle button is clicked or when the Escape key is pressed.
     */
    function dispatchExpandEvent() {
        dispatch('expanded', !expanded);
    }

    // Handle keyboard navigation
    function handleKeydown(e) {
        if (expanded && e.key === 'Escape') {
            dispatchExpandEvent();
        }
    }
</script>

<style>
    .navigation-container {
        /* Default values */
        --nav-drawer-width-default: 30rem; /* Default width for the drawer */
        --nav-drawer-collapsed-width-default: 8rem; /* Default width for the collapsed drawer */
        --drawer-transition-duration-default: 0.35s; /* Default transition duration for the drawer */
        --drawer-z-index-default: var(--layer-4); /* Default z-index for the drawer */

        /* Overridable css values. Example: --nav-drawer-width-prop="50rem" See https://v4.svelte.dev/docs/component-directives#style-props */
        --nav-drawer-width: var(--nav-drawer-width-prop, var(--nav-drawer-width-default));
        --nav-drawer-collapsed-width: var(--nav-drawer-collapsed-width-prop, var(--nav-drawer-collapsed-width-default));
        --drawer-transition-duration: var(--drawer-transition-duration-prop, var(--drawer-transition-duration-default));
        --drawer-z-index: var(--drawer-z-index-prop, var(--drawer-z-index-default));

        --drawer-bg: var(--color-gs-light-alternative-bg);
        --drawer-border: var(--color-gs-light-secondary);

        position: relative;
        pointer-events: none;
        z-index: var(--drawer-z-index);
        flex-shrink: 0; /* Prevent shrinking in flex layout when the screen is narrow */
        min-height: 100dvh; /* Ensure the drawer covers the full height of the viewport */

        /* Control the width that affects the flex layout */
        transition: width var(--drawer-transition-duration) ease-in-out;

        &.pushMode.expanded {
            width: var(--nav-drawer-width);
        }
        /* Collapsed state in push mode */
        &.pushMode:not(.expanded) {
            width: var(--nav-drawer-collapsed-width);
        }

        /* When not in push mode, take up no space in flex layout */
        &:not(.pushMode) {
            width: 0;
        }
    }
    /* Overlay styles - only visible when expanded and in overlay mode */
    .overlay {
        --overlay-transition-duration: 0.35s;
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: var(--color-overlay);
        transition: opacity var(--overlay-transition-duration), display var(--overlay-transition-duration) ease-in-out;
        transition-behavior: allow-discrete; /* Allows discrete transitions for opacity and display */
        opacity: 0;
        display: none;
        z-index: 1; /* Ensure overlay is under the drawer */

        /* Only show overlay in overlay mode */
        &.expanded:not(.pushMode) {
            opacity: 1;
            @starting-style {
                /* This with transition-behavior: allow-discrete; allows the overlay to fade in smoothly */
                opacity: 0;
            }
            display: block;
        }
    }

    /* Drawer styles for both modes */
    .drawer {
        --drawer-header-height: 7rem;

        position: absolute;
        top: 0;
        height: 100%;
        background-color: var(--drawer-bg);
        transition: all var(--drawer-transition-duration) ease-in-out;
        pointer-events: auto;
        z-index: calc(var(--layer-4) + 1); /* Keep drawer above the overlay */
        will-change: width;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        width: var(--nav-drawer-width);

        /* Collapsed state in push mode */
        &.pushMode:not(.expanded) {
            width: var(--nav-drawer-collapsed-width);
        }

        /* Collapsed state in overlay mode */
        &:not(.pushMode):not(.expanded) {
            width: 0;
        }

        &.start {
            inset-inline-start: 0;
            border-inline-end: 1px solid var(--drawer-border);
            border-inline-start: none;

            /* Header section positioning based on side */
            & header {
                flex-direction: row-reverse;
            }
            &:not(.pushMode) {
                border-inline-end: none;
            }
        }

        &.end {
            inset-inline-end: 0;
            border-inline-start: 1px solid var(--drawer-border);
            border-inline-end: none;

            /* Header section positioning based on side */
            & header {
                flex-direction: row;
            }
            &:not(.pushMode) {
                border-inline-start: none;
            }
        }

        /* Drawer header section */
        & header {
            flex-basis: var(--drawer-header-height);
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 1rem 2rem 0;
            background-color: var(--drawer-bg);
            & :global(button) {
                width: 5rem;
                height: 5rem;
            }
        }

        &.pushMode:not(.expanded) {
            & header {
                padding: 1rem var(--space-1x5) 0;
            }
        }

        /* Drawer content section */
        & .drawer-content-wrapper {
            flex-grow: 1;
            overflow: hidden;
        }
    }
</style>

<div
    {id}
    class="navigation-container"
    class:expanded
    class:pushMode
    {role}
    aria-expanded={expanded ? 'true' : 'false'}
    aria-hidden={!expanded && !pushMode ? '' : void 0}
    aria-label={ariaLabel}
    aria-labelledby={ariaLabelledBy}
    use:focusTrap={{ enabled: expanded && !pushMode }}
    on:keydown={handleKeydown}>
    <!-- The overlay overlay that appears behind the drawer when expanded (only shown in overlay mode) -->
    <div
        class="overlay"
        class:expanded
        class:pushMode
        style="pointer-events: {expanded ? 'auto' : 'none'}"
        on:keydown={handleKeydown}
        on:click|self={dispatchExpandEvent} />

    <!-- The drawer is used in both: push and overlay modes -->
    <nav
        class="drawer {side}"
        class:expanded
        class:pushMode
        {ariaLabel}
        aria-hidden={!expanded && !pushMode ? '' : void 0}>
        <header>
            <div class="icon-wrapper" bind:this={drawerCloserElement}>
                <IconBarButton
                    label={expanded ? __('Collapse Menu') : __('Expand Menu')}
                    size="base-16"
                    icon={expanded
                        ? side === 'end'
                            ? 'drawer-right-16'
                            : 'drawer-left-16'
                        : side === 'end'
                        ? 'drawer-left-16'
                        : 'drawer-right-16'}
                    shape="circular"
                    on:click={dispatchExpandEvent} />
            </div>
        </header>
        <div class="drawer-content-wrapper">
            <slot />
        </div>
    </nav>
</div>
